import { useState, useCallback, useEffect } from "react";

const canUseNotifications = (): boolean => {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    window.isSecureContext
  );
};

export interface IBrowserNotificationHook {
  permission: NotificationPermission;
  requestPermission: () => Promise<void>;
  showNotification: (title: string, options?: NotificationOptions) => void;
}

export const useBrowserNotifications = (): IBrowserNotificationHook => {
  const [permission, setPermission] =
    useState<NotificationPermission>("default");

  useEffect(() => {
    if (canUseNotifications()) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!canUseNotifications()) return;

    const status = await Notification.requestPermission();
    setPermission(status);
  }, []);

  const showNotification = useCallback(
    (title: string, options?: NotificationOptions) => {
      if (!canUseNotifications() || permission !== "granted") {
        return;
      }
      try {
        new Notification(title, options);
      } catch (error) {
        console.error("Error showing notification:", error);
      }
    },
    [permission]
  );

  return { permission, requestPermission, showNotification };
};
